import { Component, OnInit, Input, EventEmitter, Output, SimpleChanges, OnDestroy } from '@angular/core';
import { MasterService } from '../../../../../services/master.service'
import { MessageService } from '../../../../../services/message.service'
import * as languageLibrary from '../../../../../services/language'
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { formatCurrency, formatPercent } from 'src/app/utils/format';
import { Subscription } from 'rxjs';

@Component({
    selector: 'employee-commission-setup',
    templateUrl: './commission.component.html',
    styleUrls: ['./commission.component.scss'],
    standalone: false
})
export class CommissionComponent implements OnInit, OnDestroy {
    /*
    * variables
    */
    //  

    @Input() isFlat: boolean = false

    @Input() contactId: 0
    protected expanded: boolean = true

    // save the language
    protected language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    protected words = languageLibrary.language

    protected fakeData = [
        { name: "RATE on profit applied to Cash and Outside Finance deals", value: 0, calc: "rate", note: null },
        { name: "FLAT Amount per deal", value: 0, calc: "flat", note: "Overrides commission rate above." },
        { name: "RATE on DOWN PAYMENT applied to BHPH deals", value: 0, calc: "rate", note: null },
        { name: "RATE on DEFERRED PAYMENTS applied to BHPH deals", value: 0, calc: "rate", note: null },
        { name: "RATE on PAYMENTS applied to BHPH deals", value: 0, calc: "rate", note: null },
        { name: "RATE on Back-End profits", value: 0, calc: "rate", note: null },
        { name: "FLAT Amount per deal", value: 0, calc: "flat", note: "Overrides commission rate above." },
    ]

    protected commissionForm = new FormGroup({
        id: new FormControl<number>(0),
        salesRateOnProfitCashOutside: new FormControl<string>({ value: '0', disabled: true }),
        salesFlatPerDeal: new FormControl<string>({ value: '0', disabled: true }),
        salesRateDownPaymentBHPH: new FormControl<string>({ value: '0', disabled: true }),
        salesRateDeferredPaymentBHPH: new FormControl<string>({ value: '0', disabled: true }),
        salesRatePaymentBHPH: new FormControl<string>({ value: '0', disabled: true }),
        fiRateOnBEProfit: new FormControl<string>({ value: '0', disabled: true }),
        fiFlatPerDeal: new FormControl<string>({ value: '0', disabled: true }),
        frontEnd: new FormControl<boolean>(false),
        backEnd: new FormControl<boolean>(false),
    })

    private subscriptions: Subscription[] = []
    /*
    * Life cicles
    */
    constructor(private master: MasterService, private ms: MessageService, private route: ActivatedRoute, private fb: FormBuilder) {

    }

    ngOnInit() {
        this.subscriptions.push(
            this.commissionForm.get("frontEnd").valueChanges.subscribe(value => {
                if (value) {
                    this.commissionForm.get("salesRateOnProfitCashOutside").enable()
                    this.commissionForm.get("salesFlatPerDeal").enable()
                    this.commissionForm.get("salesRateDownPaymentBHPH").enable()
                    this.commissionForm.get("salesRatePaymentBHPH").enable()
                    this.commissionForm.get("salesRateDeferredPaymentBHPH").enable()
                    return
                }
                this.commissionForm.get("salesRateOnProfitCashOutside").disable()
                this.commissionForm.get("salesFlatPerDeal").disable()
                this.commissionForm.get("salesRateDownPaymentBHPH").disable()
                this.commissionForm.get("salesRatePaymentBHPH").disable()
                this.commissionForm.get("salesRateDeferredPaymentBHPH").disable()
            })
        )
        this.subscriptions.push(
            this.commissionForm.get("backEnd").valueChanges.subscribe(value => {
                if (value) {
                    this.commissionForm.get("fiRateOnBEProfit").enable()
                    this.commissionForm.get("fiFlatPerDeal").enable()
                    return
                }
                this.commissionForm.get("fiRateOnBEProfit").disable()
                this.commissionForm.get("fiFlatPerDeal").disable()
            })
        )
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes) {
            if (changes.contactId) {
                this.contactId = changes.contactId.currentValue
                this.getCommission()
            }
        }
    }

    protected switchFlatOrRate(field) {
        if (field == 'salesRateOnProfitCashOutside') {
            this.commissionForm.get('salesFlatPerDeal').setValue(formatCurrency(0))
        }
        
        if (field == 'salesFlatPerDeal') {
            this.commissionForm.get('salesRateOnProfitCashOutside').setValue(formatCurrency(0))
        }

        if (field == 'fiRateOnBEProfit') {
            this.commissionForm.get('fiFlatPerDeal').setValue(formatCurrency(0))
        }

        if (field == 'fiFlatPerDeal') {
            this.commissionForm.get('fiRateOnBEProfit').setValue(formatCurrency(0))
        }
    }

    protected getCommission() {
        if (!this.contactId || (this.contactId <= 0)) {
            return
        }

        this.master.get(`contacts/${this.contactId}/commission`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.commission || res.data.commission.length == 0) {
                return
            }

            this.setCommissionGeneralInfoForm(res.data.commission[0])
        })
    }

    protected saveCommission() {

        if (!this.contactId || (this.contactId <= 0) || this.commissionForm.invalid) {
            return
        }

        const identity = this.commissionForm.get('id').value
        if (!identity || isNaN(identity) || (identity <= 0)) {
            this.createCommission()
            return
        }

        this.modifyCommission()
    }

    protected createCommission() {
        if (!this.contactId || (this.contactId <= 0)) {
            return
        }

        const information = this.commissionForm.getRawValue()

        this.master.post(`contacts/${this.contactId}/commission`, { information }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.commission) {
                return
            }

            this.setCommissionGeneralInfoForm(res.data.commission)
        })
    }

    protected modifyCommission() {
        if (!this.contactId || (this.contactId <= 0)) {
            return
        }
        const information = this.commissionForm.getRawValue()

        this.master.put(`contacts/${this.contactId}/commission/${this.commissionForm.get('id').value}`, { information }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

        })
    }

    protected setCommissionGeneralInfoForm(commission: any) {
        if (!commission) {
            return
        }

        this.commissionForm.patchValue({
            id: commission.id,
            salesRateOnProfitCashOutside: commission.salesRateOnProfitCashOutside,
            salesFlatPerDeal: commission.salesFlatPerDeal,
            salesRateDownPaymentBHPH: commission.salesRateDownPaymentBHPH,
            salesRateDeferredPaymentBHPH: commission.salesRateDeferredPaymentBHPH,
            salesRatePaymentBHPH: commission.salesRatePaymentBHPH,
            fiRateOnBEProfit: commission.fiRateOnBEProfit,
            fiFlatPerDeal: commission.fiFlatPerDeal,
            frontEnd: commission.frontEnd,
            backEnd: commission.backEnd
        })
    }

}
